import React, {useEffect, useState, useCallback} from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Snackbar from '@mui/material/Snackbar';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import {XIcon} from 'lucide-react';
import {connect, useDispatch, useSelector} from 'react-redux';
import _ from 'lodash';
import {withRequiredAuthInfo} from '@propelauth/react';
import {withLDConsumer, useFlags} from 'launchdarkly-react-client-sdk';
import PropTypes from 'prop-types';

import {REPORT_TABS} from './constants';
import {getMinPeriodDate} from '../../util/date';
import ReportPeriodSelect from '../../components/ReportPeriodSelect/ReportPeriodSelect';
import ReportTabs from './ReportTabs';
import AppPage from '../AppPage';
import CommonPageWrapper from '../../components/CommonPageWrapper';
import ConfiguredReport from '../../components/financialStatements/ConfiguredReport';
import DownloadReport from '../../components/DownloadReport';
import ReportsMarketingLander from '../../components/ReportsMarketingLander';
import {
  useGetOrCreateFluxAnalysisMutation,
  useLazyGetFluxAnalysisChangesQuery,
} from '../../data/copilotV2Api';
import {useGetReportsListQuery} from '../../data/api';
import {setSelectedComparePeriod, setSelectedPeriod} from '../../data/periods';
import {
  setComparePeriod,
  setCurrentPeriod,
  setFluxAnalysisLoading,
  setFluxChangesLoading,
} from '../../data/reports';
import {useParamReportPeriodOrDefault} from '../../util/useParamReportPeriodOrDefault';
import {FluxSwitch} from '../../components/FluxSwitch';

const FLUX_REPORT_TYPES = [
  'BALANCE_SHEET',
  'INCOME_STATEMENT_GAAP',
  'INCOME_STATEMENT_NON_GAAP',
  // Intentionally left out because often times the data doesn't exist and causes error notifications to pop up
  // 'INDIRECT_CASHFLOW',
];

const CHANGES_REPORT_TYPES = [...FLUX_REPORT_TYPES, 'INDIRECT_CASHFLOW'];

class ReportsPage extends AppPage {
  constructor(props) {
    super(props);

    this.state = {
      reports: [],
      compareReports: [],
      reportType: 'BALANCE_SHEET',
      lastSyncedAt: null,
    };
  }

  setReportType = (_, reportType) => {
    this.setState({reportType});
  };

  getPageTitle = () => {
    if (!this.props.flags.statementsDashboard) {
      return;
    }

    return 'Statements';
  };

  handlePeriodChange = (period) => {
    this.props.setSelectedPeriod({period});
    this.props.setCurrentPeriod({currentPeriod: period});
  };

  handleComparePeriodChange = (period) => {
    this.props.setSelectedComparePeriod({comparePeriod: period});
    this.props.setComparePeriod({comparePeriod: period});
  };

  // computes the earliest date that we have data for in the reporting periods

  renderCustomHeaderContent = () => {
    const {orgConfig} = this.props.session;
    if (!this.props.flags.statementsDashboard) return;

    const disabled =
      !this.props.periods.reportingPeriods?.length ||
      this.props.periods.loading;

    return (
      <Stack
        direction={'row'}
        flex={1}
        alignItems={'center'}
        justifyContent={'space-between'}
        height="100%"
      >
        <Box flex={0.5} />
        <Stack alignItems="center" flex={1}>
          <ReportPeriodSelect
            onPeriodChange={this.handlePeriodChange}
            onComparePeriodChange={this.handleComparePeriodChange}
            selectedPeriod={this.props.periods.selectedPeriod}
            selectedComparePeriod={this.props.periods.selectedComparePeriod}
            loading={this.props.periods.loading}
            disabled={disabled}
            minCalendarDate={getMinPeriodDate(this.props.periods)}
            fiscalYearEnd={orgConfig?.fiscalYearEnd}
          />
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          flex={1}
          justifyContent="flex-end"
        >
          {!_.isEmpty(this.state.reports) && this.state.lastSyncedAt && (
            <Typography sx={{color: '#666', fontSize: '0.75rem'}}>
              <div>Last synced: </div>
              {(() => {
                const lastSynced = new Date(this.state.lastSyncedAt);
                return (
                  lastSynced.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                  }) +
                  ' @ ' +
                  lastSynced
                    .toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                    })
                    .toLowerCase()
                );
              })()}
            </Typography>
          )}

          {this.props.flags.fluxAnalysis && <FluxSwitch />}
          <DownloadReport
            visible={
              !_.isEmpty(this.state.reports) &&
              (!_.has(
                this.props.session.currentOrg,
                'org_metadata.hideDownload'
              ) ||
                this.props.session.currentOrg.org_metadata.hideDownload ===
                  false)
            }
            reports={this.state.reports}
          />
        </Stack>
      </Stack>
    );
  };

  renderSubHeaderContent = () => {
    if (!this.props.flags.statementsDashboard) {
      return null;
    }

    return (
      <ReportTabs
        selectedReport={this.state.reportType}
        onChange={this.setReportType}
        reports={REPORT_TABS.filter((t) => !t.hidden).map((r) => ({
          ...r,
          disabled:
            r.reportType === 'INDIRECT_CASHFLOW'
              ? Boolean(!this.props.flags.indirectCashFlow)
              : false,
        }))}
      />
    );
  };

  renderBodyContent = () => {
    return (
      <ReportsPageComponent
        reportType={this.state.reportType}
        setReports={(_reports) => this.setState({reports: _reports})}
        setLastSyncedAt={(lastSyncedAt) => this.setState({lastSyncedAt})}
      />
    );
  };
}

const ReportsPageComponent = ({reportType, setReports, setLastSyncedAt}) => {
  const dispatch = useDispatch();
  const {currentOrg, orgConfig, isAuthenticated} = useSelector(
    (state) => state.session
  );
  const {statementsDashboard: statementsEnabled, statementsAlert} = useFlags();
  const comparePeriod = useSelector(
    (state) => state.periods.selectedComparePeriod
  );
  const fluxAnalysisActive = useSelector(
    (state) => state.reports.fluxAnalysisActive
  );

  const period = useParamReportPeriodOrDefault();
  useEffect(() => {
    dispatch(setSelectedPeriod({period}));
  });

  const [skip, setSkip] = useState(true);
  const {data, refetch, isError, isSuccess} = useGetReportsListQuery(
    {period, comparePeriod},
    {skip}
  );
  const [loadingReports, setLoadingReports] = useState(true);
  const [reports, setLocalReports] = useState(null);

  useEffect(() => {
    setLoadingReports(true);
  }, [period, comparePeriod]);

  const [showFluxError, setShowFluxError] = useState(false);
  const [getOrCreateFluxAnalysis] = useGetOrCreateFluxAnalysisMutation();
  const [getFluxAnalysisChanges] = useLazyGetFluxAnalysisChangesQuery();
  const [fluxMapping, setFluxMapping] = useState(null);
  const [fluxAnalysisRequests, setFluxAnalysisRequests] = useState([]);
  const [fluxChangesRequests, setFluxChangesRequests] = useState([]);

  const runChangesRequests = useCallback(() => {
    fluxChangesRequests.forEach((p) => p.abort());
    dispatch(setFluxChangesLoading({fluxChangesLoading: true}));

    const rounding = orgConfig?.roundNumbersTo || 'ones';
    // Gets the absolute and percent values
    const changesPromises = CHANGES_REPORT_TYPES.map((reportType) =>
      getFluxAnalysisChanges({
        period,
        comparePeriod,
        reportType,
        rounding,
      })
    );
    setFluxChangesRequests(changesPromises);

    Promise.allSettled(changesPromises)
      .then((results) => {
        const mapping = {};
        results.forEach(({value: {data}}) => {
          if (data) {
            mapping[data.report_type] = {};
            data.changes.forEach((item) => {
              mapping[data.report_type][item.pli_id] = {
                pliId: item.pli_id,
                percentChange: item.percent_change,
                absoluteChange: item.absolute_change,
              };
            });
          }
        });
        setFluxMapping((prev) => _.merge(prev, mapping));
      })
      .finally(() => {
        dispatch(setFluxChangesLoading({fluxChangesLoading: false}));
      });
  }, [fluxMapping, period, comparePeriod]);

  const runAnalysisRequests = useCallback(() => {
    fluxAnalysisRequests.forEach((p) => p.abort());
    dispatch(setFluxAnalysisLoading({fluxAnalysisLoading: true}));

    const rounding = orgConfig?.roundNumbersTo || 'ones';
    // Gets the analysis data
    const analysisPromises = FLUX_REPORT_TYPES.map((reportType) =>
      getOrCreateFluxAnalysis({
        period,
        comparePeriod,
        reportType,
        rounding,
      })
    );
    setFluxAnalysisRequests(analysisPromises);
    Promise.allSettled(analysisPromises)
      .then((results) => {
        const mapping = {};
        let hasError = false;
        results.forEach(({value: {data, error}}) => {
          if (data) {
            mapping[data.report_type] = {};
            data.flux.forEach((item) => {
              mapping[data.report_type][item.pli_id] = {
                pliId: item.pli_id,
                analysis: item.analysis,
              };
            });
          } else if (error) {
            hasError = true;
          }
        });
        setShowFluxError(hasError);
        setFluxMapping((prev) => _.merge(prev, mapping));
      })
      .finally(() => {
        dispatch(setFluxAnalysisLoading({fluxAnalysisLoading: false}));
      });
  }, [fluxMapping, period, comparePeriod]);

  useEffect(() => {
    if (!period || !comparePeriod || !fluxAnalysisActive) {
      return;
    }

    setFluxMapping(null);
    runChangesRequests();
    runAnalysisRequests();
  }, [period, comparePeriod, fluxAnalysisActive, orgConfig?.roundNumbersTo]);

  useEffect(() => {
    if (!data) {
      return;
    }

    const _allReports = _.get(data, 'reports', []);
    const _reports = _.defaultTo(_.find(_allReports, {period}), {
      data: [],
    }).data;

    setLocalReports(_reports);
    setReports(_reports);

    setLastSyncedAt(data.lastSyncedAt);
    setLoadingReports(false);
  }, [data, orgConfig?.roundNumbersTo]);

  useEffect(() => {
    setSkip(
      _.isNil(period) ||
        _.isEmpty(period) ||
        _.isNil(currentOrg) ||
        _.isEmpty(currentOrg) ||
        !isAuthenticated
    );
  }, [period, currentOrg, isAuthenticated]);

  useEffect(() => {
    if (isSuccess) {
      refetch();
    }
  }, []);

  if (!statementsEnabled && currentOrg.orgName) {
    return <ReportsMarketingLander orgName={currentOrg.orgName} />;
  }

  const activeReport = _.find(
    reports,
    (_report) => _report.reportType === reportType
  );

  return (
    <CommonPageWrapper>
      <Stack
        direction="column"
        flex={1}
        justifyContent="flex-start"
        height="100%"
        sx={{pb: statementsAlert ? 6 : 0}}
      >
        {statementsAlert && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              borderRadius: 0,
              '& .MuiAlert-message': {
                fontSize: '0.875rem',
              },
            }}
          >
            We&apos;re currently experiencing issues with our NetSuite
            integration. Financial statements data may be delayed or inaccurate.
            Please contact support if you notice any discrepancies.
          </Alert>
        )}
        <Box sx={{flex: 1, minHeight: 0}}>
          <ConfiguredReport
            refetch={refetch}
            visible
            fluxMapping={fluxMapping}
            onFluxPatch={({reportType, pliId, analysis}) => {
              setFluxMapping((prev) => ({
                ...prev,
                [reportType]: {
                  ...(prev[reportType] || {}),
                  [pliId]: {
                    ...(prev[reportType]?.[pliId] || {}),
                    pliId,
                    analysis,
                  },
                },
              }));
            }}
            loading={loadingReports}
            hasError={isError}
            report={activeReport}
            title={activeReport?.title}
            depthCorrection={activeReport?.depthCorrection}
            period={period}
            comparePeriod={comparePeriod}
          />
        </Box>
      </Stack>
      <Snackbar
        open={showFluxError}
        onClose={() => setShowFluxError(false)}
        message={
          <Stack width="100%">
            <Stack
              width="100%"
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              pb={1}
            >
              <Stack
                direction="row"
                alignItems="center"
                pr={4}
                gap={1}
                width="100%"
              >
                <Box
                  sx={{
                    backgroundColor: '#E85835 !important',
                    borderRadius: '100%',
                    width: 20,
                    height: 20,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <XIcon color="white" size={12} />
                </Box>
                <Typography fontWeight={500} fontSize={14}>
                  Something went wrong
                </Typography>
              </Stack>
              <IconButton size="small" onClick={() => setShowFluxError(false)}>
                <XIcon size={16} />
              </IconButton>
            </Stack>
            <Typography whiteSpace="wrap" fontSize={12} pl={3.5}>
              There was an error retrieving one or more flux analysis.
            </Typography>
          </Stack>
        }
        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
        sx={{
          '& .MuiPaper-root': {
            backgroundColor: '#FFFFFF',
            color: '#000000',
            boxShadow: '0px 1px 8px 0px #00000029',
            fontWeight: '500',
            fontSize: '14px',
            lineHeight: '100%',
            letterSpacing: '0%',
          },
        }}
      />
    </CommonPageWrapper>
  );
};

ReportsPageComponent.propTypes = {
  reportType: PropTypes.string.isRequired,
  setReports: PropTypes.func.isRequired,
  setLastSyncedAt: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
  return {
    periods: state.periods,
    session: state.session,
    reports: state.reports,
  };
};

export default withLDConsumer()(
  connect(mapStateToProps, {
    setSelectedPeriod,
    setSelectedComparePeriod,
    setCurrentPeriod,
    setComparePeriod,
  })(withRequiredAuthInfo(ReportsPage))
);
